import React from "react";
import { ImpactShopProductType } from "@ecologi/shared";
import {
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
} from "next";
import { ImpactShopProductPage } from "../../../features";
import { auth0, getImpactShopProduct } from "../../../lib";
import { withLogging } from "../../../lib/ssr/logGetServerSideProps";
import { createServerTRPCClient } from "../../../lib/trpc/server";
import { prefetchImpactGroupsForSSR } from "../../../features/impact-shop/hooks/useListImpactGroups";
import { QueryCache } from "react-query";
import { dehydrate } from "react-query/hydration";

type ImpactShopProductPageComponentProps = {
  isLoggedIn: boolean;
  product: ImpactShopProductType;
  impactGroupsEnabled?: boolean;
};

const ImpactShopProductPageComponent = ({
  product,
  isLoggedIn,
  impactGroupsEnabled,
}: ImpactShopProductPageComponentProps) => (
  <ImpactShopProductPage
    isLoggedIn={isLoggedIn}
    product={product}
    impactGroupsEnabled={impactGroupsEnabled}
  />
);

const redirectToImpactShop = {
  redirect: {
    destination: "/impact-shop",
    permanent: false,
  },
} as const;

export const getServerSideProps = withLogging(
  async (context: GetServerSidePropsContext) => {
    const tokenCache = auth0.tokenCache(
      context.req as NextApiRequest,
      context.res as NextApiResponse
    );

    const session = await auth0.getSession(context.req);

    try {
      const product = await getImpactShopProduct(
        context.params.slug as string,
        session ? tokenCache : undefined
      );

      if (!product) {
        return redirectToImpactShop;
      }

      const queryCache = new QueryCache();
      let dehydratedState: ReturnType<typeof dehydrate> | undefined;

      if (session) {
        try {
          const serverTrpc = createServerTRPCClient(tokenCache);
          await prefetchImpactGroupsForSSR(queryCache, serverTrpc);
          dehydratedState = dehydrate(queryCache);
        } catch (error) {
          console.error("Failed to prefetch impact groups:", error);
        }
      }

      return {
        props: {
          layoutProps: {
            hideHeader: true,
            isWhiteBody: true,
            isProfile: true,
          },
          isLoggedIn: Boolean(session?.user),
          product,
          ...(dehydratedState && { dehydratedState }),
        },
      };
    } catch (e) {
      return redirectToImpactShop;
    }
  }
);

export default ImpactShopProductPageComponent;
